// public/js/features/tracker-config/battery-usage.js
// Remaining battery life per DEVICE, from what the device actually did rather
// than from what its configuration says it would do. docs/battery-remaining.md.
//
// Pure module, no browser APIs: the Lambda imports it to BOOK energy at ingest
// (uplinkCost) and the frontend imports it to READ the estimate off a device
// row (estimateRemaining). One file, one set of coefficients - the table in
// battery.js - so the two sides cannot drift.
//
// VERSIONED. Every booking and every estimate carries BATTERY_MODEL_VERSION, and
// the admin recompute endpoint rebuilds every device from its telemetry with the
// current model. That is the improvement path: change a coefficient or a rule
// here, bump the version, recompute - nothing has to be migrated by hand.
//
// v1 assumptions, each a candidate for improvement once real end-of-life data
// exists (see "Fleet learning" in the doc):
//   * every record carrying a position was one positioning attempt at the
//     family's GNSS cost - Wi-Fi-first Edge devices are charged GNSS anyway,
//     because we cannot tell from the record which one succeeded;
//   * a record with fixOk === false and no position still cost a GNSS attempt;
//   * one BLE scan per uplink that carried tag data, at a 5 s window;
//   * sleep and self-discharge accrue for the gap since the previous uplink,
//     capped at 7 days - beyond that we cannot know whether the device was
//     asleep or switched off;
//   * usable capacity is the family capacity times the same 85 % conditions
//     factor the configuration estimator uses.

import { BATTERY_FAMILIES, familyForModel, GPS_FIX_S } from './battery.js';

export const BATTERY_MODEL_VERSION = 3;

// ── BLE beacons: time-based, no event log ────────────────────────────────────
// A beacon advertises at a fixed interval, so its consumption per day is a
// CONSTANT from the interval and the cell - there is nothing to book per uplink.
// Remaining life is therefore rated life minus time since first contact, and
// for families that report a real voltage the coin-cell curve caps it once the
// cell starts to drop. Every number here is an engineering assumption, flagged
// as such in the UI; the manufacturer's own figures replace them when we have
// them (same path as the tracker constants - edit, bump the version, recompute).
//
//   capacityMAh    nominal cell capacity (CR2450 ~620 mAh)
//   advIntervalS   the interval our fleet is configured with (docs/ble-sensors.md)
//   advUAs         charge per advertisement event, microamp-seconds
//   sleepUA        quiescent current, microamps
//   shelfYears     the cell's shelf life - a hard cap whatever the interval
//   ratedDays      used INSTEAD of the energy model when we only have a rated life
//   reportsVoltage whether `battery` on the row is a real reading
//   vFull/vEmpty   the coin-cell curve's ends for the voltage cap
// ── ELA Blue Puck: the manufacturer's own life curves ────────────────────────
// Document "BLE PUCK battery life cycle 03E EN", archived under
// docs/datasheets/. These beacons have no event log and report no voltage, so
// there is nothing to book per uplink; what decides their life is the
// advertising period first, temperature second, transmit power third - all
// three configuration, not behaviour. So this family is modelled from the
// published curves instead of from an energy budget.
//
// ELA publishes charts, not tables, so the points below are READ OFF those
// charts: treat them as +/- a few tenths of a year, and prefer a table if ELA
// ever publishes one. Period in seconds -> life in years.
//
// The combination rule is ELA's own (section 10 of that document): read the
// product's curve at the 0 dBm / +23 C reference, then divide by the ratio the
// reference product (Blue Puck ID) shows between those reference conditions and
// the actual ones. Their worked example reproduces exactly with the helpers
// below, which is the check that this is implemented as intended.
const ELA_RHT_0DBM_23C = [[0.1, 0.4], [0.5, 2.2], [1, 4.0], [2, 6.5], [3, 8.7], [4, 10.4],
                          [5, 11.9], [6, 13.3], [7, 14.6], [8, 15.8], [9, 17.0], [10, 18.0]];
// Blue Puck ID at 0 dBm, one curve per ambient temperature (section 2).
const ELA_ID_0DBM_BY_C = [
  [-40, [[3, 0.09], [10, 0.16]]],                                                   // footnote, not a drawn curve
  [-30, [[0.1, 0.2], [0.5, 0.75], [1, 1.35], [2, 2.3], [3, 3.0], [5, 3.95], [7.5, 4.75], [10, 5.0]]],
  [-18, [[0.1, 0.5], [0.5, 2.3], [1, 4.15], [2, 6.9], [3, 9.1], [5, 11.55], [7.5, 13.7], [10, 14.0]]],
  [23,  [[0.1, 0.9], [0.5, 3.7], [1, 6.6], [2, 11.0], [3, 14.4], [5, 18.4], [7.5, 20], [10, 20]]],
  [80,  [[0.1, 0.75], [0.5, 3.1], [1, 5.6], [2, 9.3], [3, 12.2], [5, 15.65], [7.5, 18.45], [10, 19.0]]],
];
// Blue Puck ID at +4 dBm, +23 C (section 3) - the transmit-power reference.
const ELA_ID_4DBM_23C = [[0.1, 0.8], [0.5, 3.2], [1, 5.7], [2, 9.8], [3, 12.6], [5, 16.8], [7.5, 20], [10, 20]];
// Self-discharge and the chemistry itself cap the cell at 20 years INCLUDING
// shelf time, whatever the duty cycle says (section 1).
const ELA_SHELF_YEARS = 20;

/** Linear interpolation on a [[x, y], ...] curve, clamped at both ends. */
function onCurve(curve, x) {
  if (x <= curve[0][0]) return curve[0][1];
  const last = curve[curve.length - 1];
  if (x >= last[0]) return last[1];
  for (let i = 1; i < curve.length; i += 1) {
    const [x0, y0] = curve[i - 1], [x1, y1] = curve[i];
    if (x <= x1) return y0 + (y1 - y0) * ((x - x0) / (x1 - x0));
  }
  return last[1];
}

/** Reference-product life at one period and ambient, interpolated between the
 *  published temperature curves. */
function elaIdYears(periodS, tempC) {
  const t = Math.max(-40, Math.min(80, Number(tempC)));
  let lo = ELA_ID_0DBM_BY_C[0], hi = ELA_ID_0DBM_BY_C[ELA_ID_0DBM_BY_C.length - 1];
  for (let i = 1; i < ELA_ID_0DBM_BY_C.length; i += 1) {
    if (t <= ELA_ID_0DBM_BY_C[i][0]) { lo = ELA_ID_0DBM_BY_C[i - 1]; hi = ELA_ID_0DBM_BY_C[i]; break; }
  }
  const yLo = onCurve(lo[1], periodS), yHi = onCurve(hi[1], periodS);
  if (hi[0] === lo[0]) return yLo;
  return yLo + (yHi - yLo) * ((t - lo[0]) / (hi[0] - lo[0]));
}

/**
 * Rated life in YEARS for an ELA Blue Puck RHT, by ELA's own combination rule.
 * Defaults are what our fleet is configured to (docs/ble-sensors.md).
 */
export function elaRatedYears({ advIntervalS = 5, txDbm = 4, tempC = 23 } = {}) {
  const base   = onCurve(ELA_RHT_0DBM_23C, advIntervalS);         // product, reference conditions
  const idRef  = onCurve(ELA_ID_0DBM_BY_C[3][1], advIntervalS);   // Blue Puck ID at 23 C, 0 dBm
  const idTemp = elaIdYears(advIntervalS, tempC);
  const tempFactor  = idTemp > 0 ? idRef / idTemp : Infinity;
  const powerFactor = txDbm >= 4 ? idRef / onCurve(ELA_ID_4DBM_23C, advIntervalS) : 1;
  return Math.min(base / tempFactor / powerFactor, ELA_SHELF_YEARS);
}

/**
 * The fraction of TOTAL life that `days` spent at `tempC` consumes.
 *
 * Integrating this is the whole point: averaging the temperature and looking up
 * one life would be wrong, because life vs temperature is steeply non-linear. A
 * tag that spends half its time at -30 C and half at +23 C does NOT live as long
 * as one held at the -3.5 C average - it lives 0.5/2.3 + 0.5/10.9 per year, so
 * about 3.8 years against the 9-ish the average would suggest. Rates add;
 * temperatures do not.
 */
export function elaLifeFraction(days, tempC, fam = null) {
  const f = fam || BLE_FAMILIES.elaRht;
  const years = elaRatedYears({ advIntervalS: f.advIntervalS, txDbm: f.txDbm, tempC });
  return years > 0 ? days / (years * 365) : 0;
}

export const BLE_FAMILIES = {
  ingicsIbs03r: { label: 'Superb Engine+', cell: 'CR2450', capacityMAh: 620, advIntervalS: 30, advUAs: 25, sleepUA: 3, selfPctYr: 1, shelfYears: 10, reportsVoltage: true,  vFull: 2.95, vEmpty: 2.40, estimated: true },
  ingicsIbs09r: { label: 'Superb Engine+', cell: 'CR2450', capacityMAh: 620, advIntervalS: 30, advUAs: 25, sleepUA: 3, selfPctYr: 1, shelfYears: 10, reportsVoltage: true,  vFull: 2.95, vEmpty: 2.40, estimated: true },
  // ELA RHT: the tracker's tag format carries no battery field, so the `battery`
  // on its row is a placeholder and the life comes from the curves above.
  // advIntervalS/txDbm are HOW WE CONFIGURE THE FLEET, not datasheet maxima -
  // change them here when the fleet setting changes, bump the model version and
  // recompute. At 5 s / +4 dBm this works out at ~10.9 years, against the 18 the
  // datasheet headline quotes for its own best case (10 s at 0 dBm).
  elaRht:       { label: 'Superb Temp+',   cell: 'Sealed lithium', curve: 'elaRht', advIntervalS: 5, txDbm: 4, reportsVoltage: false, estimated: true },
};

const BLE_SUBTYPE_TO_FAMILY = { 'Ingics-iBS03R': 'ingicsIbs03r', 'Ingics-iBS09R': 'ingicsIbs09r', 'ELA-RHT': 'elaRht' };

/** Rated life in days for a BLE family - from a manufacturer curve, the energy
 *  model, or a flat rated figure, in that order. */
export function bleRatedDays(fam) {
  if (fam.curve === 'elaRht') return elaRatedYears(fam) * 365;
  if (fam.ratedDays) return fam.ratedDays;
  const avgUA = fam.sleepUA + fam.advUAs / fam.advIntervalS;
  const selfUA = (fam.selfPctYr / 100 * fam.capacityMAh * 1000) / (365 * 24);   // mAh/yr -> uA
  const hours = fam.capacityMAh * 1000 / (avgUA + selfUA);
  return Math.min(hours / 24, fam.shelfYears * 365);
}

/** Coin-cell state of charge from voltage: 1 above vFull, 0 below vEmpty,
 *  piecewise in between (flat plateau, then the knee). */
export function bleSocFromVoltage(fam, v) {
  if (!fam.reportsVoltage || !(v > 0)) return null;
  if (v >= fam.vFull) return 1;
  if (v <= fam.vEmpty) return 0;
  // Plateau down to ~2.8 V holds ~80 %, then it falls away quickly.
  const knee = 2.80;
  if (v >= knee) return 0.8 + 0.2 * (v - knee) / (fam.vFull - knee);
  return 0.8 * (v - fam.vEmpty) / (knee - fam.vEmpty);
}

const CONDITIONS_PCT   = 85;    // matches defaultAssumptions() in battery.js
const BLE_WINDOW_S     = 5;     // matches the estimator's default scan window
const MAX_GAP_HOURS    = 7 * 24;
const RATE_WINDOW_DAYS = 30;
const MS_PER_DAY       = 86400000;

/** Day key (UTC) for the daily-consumption ring. */
export function dayKey(iso) {
  return String(iso || new Date().toISOString()).slice(0, 10);
}

/** Family key for a device row: BLE by subType, trackers by prodId / model. */
export function familyForDevice(device) {
  if (device?.type === 'bluetooth') return BLE_SUBTYPE_TO_FAMILY[device.subType] || null;
  const raw = device?.metadata?.prodId ?? device?.prodId ?? device?.subType ?? device?.model ?? null;
  return raw != null ? familyForModel(String(raw)) : null;
}

/** True when the family is a BLE beacon (time-based, nothing booked at ingest). */
export const isBleFamily = (familyKey) => !!BLE_FAMILIES[familyKey];

/**
 * Energy cost of ONE uplink, in mAh.
 *
 * @param {string} familyKey            key into BATTERY_FAMILIES
 * @param {object} uplink
 * @param {Array}  uplink.records       every record the uplink carried
 * @param {number} uplink.bleScans      scans in this uplink (records with tag data, or 1 if beacons were reported)
 * @param {number} uplink.hoursSincePrev hours since the previous uplink (sleep + self-discharge accrue here)
 * @returns {{ mAh:number, parts:object } | null}  null when the family is unknown
 */
export function uplinkCost(familyKey, { records = [], bleScans = 0, hoursSincePrev = 0 } = {}) {
  const fam = BATTERY_FAMILIES[familyKey];
  if (!fam) return null;

  const gpsPerFix = fam.gpsMode === 'perSecond' ? fam.cGps * GPS_FIX_S : fam.cGps;
  let fixes = 0;
  for (const r of records) {
    const hasPos = r?.lat != null && r?.lon != null;
    if (hasPos || r?.fixOk === false) fixes++;
  }

  const gap = Math.min(MAX_GAP_HOURS, Math.max(0, Number(hoursSincePrev) || 0));
  const cBle = fam.cBleScanS ?? BATTERY_FAMILIES.oysterEdge.cBleScanS;

  const parts = {
    upload:        fam.cUpload,
    records:       records.length * fam.cRecord,
    fixes:         fixes * gpsPerFix,
    ble:           bleScans * cBle * BLE_WINDOW_S,
    sleep:         fam.sleepMAhH * gap,
    selfDischarge: (fam.selfPctYr / 100 * fam.capacity) / (365 * 24) * gap,
  };
  const mAh = Object.values(parts).reduce((a, b) => a + b, 0);
  return { mAh, parts, fixes };
}

/**
 * Read the estimate off a device row. Returns null when the device has no
 * accounting yet or its family is unknown.
 *
 * The rate prefers the trailing 30-day window (so a configuration change - or a
 * deliberate burn-down test - shows up within days) and falls back to the
 * lifetime average while the window is too thin to trust.
 */
export function estimateRemaining(device, now = Date.now()) {
  const familyKey = familyForDevice(device);
  if (isBleFamily(familyKey)) return estimateBleRemaining(device, BLE_FAMILIES[familyKey], familyKey, now);
  const fam = familyKey ? BATTERY_FAMILIES[familyKey] : null;
  if (!fam || device?.battEpochAt == null) return null;

  const consumed = Number(device.battConsumedMAh) || 0;
  const uplinks  = Number(device.battUplinks) || 0;
  const usable   = fam.capacity * CONDITIONS_PCT / 100;
  const remaining = Math.max(0, usable - consumed);

  const epochMs = Date.parse(device.battEpochAt);
  const daysSinceEpoch = Math.max(1 / 24, (now - epochMs) / MS_PER_DAY);
  const rateLife = consumed / daysSinceEpoch;

  // Trailing window from the daily ring: only days inside the window, and the
  // divisor is the days the window actually covers (capped by the epoch), so a
  // device two days past its epoch is not averaged over thirty.
  const daily = device.battDaily && typeof device.battDaily === 'object' ? device.battDaily : {};
  const cutoff = dayKey(new Date(now - RATE_WINDOW_DAYS * MS_PER_DAY).toISOString());
  let windowMAh = 0;
  for (const [day, v] of Object.entries(daily)) if (day >= cutoff) windowMAh += Number(v) || 0;
  const coverageDays = Math.min(RATE_WINDOW_DAYS, daysSinceEpoch);
  const rate30 = coverageDays > 0 ? windowMAh / coverageDays : 0;

  const useWindow = coverageDays >= 3 && windowMAh > 0;
  const rate = useWindow ? rate30 : rateLife;

  const externalPower = device.externalPowerGood === true;
  const estRemainingDays = externalPower ? Infinity : (rate > 0 ? remaining / rate : Infinity);

  // Confidence is deliberately pessimistic in v1: borrowed coefficients, a
  // first-contact epoch or a thin window each pull it down. 'high' is reserved
  // for a family calibrated from real end-of-life data, which does not exist yet.
  const notes = [];
  let confidence = 'medium';
  if (fam.familyEstimated) { confidence = 'low'; notes.push(`${fam.label} coefficients are borrowed from Oyster Edge (no DM calculator yet).`); }
  if (device.battEpochKind === 'firstContact') { confidence = 'low'; notes.push('Accounting started at first contact; charge used before that is unknown.'); }
  if (coverageDays < 7) { confidence = 'low'; notes.push('Fewer than seven days of data - the rate is still settling.'); }

  return {
    modelVersion: Number(device.battModelVersion) || BATTERY_MODEL_VERSION,
    familyKey, family: fam.label,
    method: externalPower ? 'externalPower' : 'accounting',
    epochAt: device.battEpochAt, epochKind: device.battEpochKind || 'firstContact',
    daysSinceEpoch, uplinks,
    capacityMAh: fam.capacity, usableMAh: usable, consumedMAh: consumed, remainingMAh: remaining,
    consumedPct: usable > 0 ? Math.min(100, consumed / usable * 100) : 0,
    ratePerDay: rate, rateSource: useWindow ? 'window30' : 'lifetime',
    rate30, rateLife, windowCoverageDays: coverageDays,
    estRemainingDays,
    confidence, notes,
  };
}

/**
 * BLE beacon estimate: time since first contact against the rated life, and -
 * where the beacon reports a real voltage - capped by the coin-cell curve, so a
 * cell that has started to drop is believed over the calendar.
 */
function estimateBleRemaining(device, fam, familyKey, now) {
  if (device?.battEpochAt == null) return null;
  const epochMs = Date.parse(device.battEpochAt);
  const daysSinceEpoch = Math.max(1 / 24, (now - epochMs) / MS_PER_DAY);
  const capacity = fam.capacityMAh || 0;
  const usable = capacity;                       // no conditions derate on a sealed coin cell

  // A tag that reports its own ambient has its life BOOKED against the
  // temperature it actually lived at (battUsedFrac, integrated at ingest and
  // rebuilt by the recompute). Rated life then only converts what is left into
  // days, at the temperature it is sitting at now.
  const usedFrac = Number(device.battUsedFrac);
  const hasIntegrated = fam.curve === 'elaRht' && Number.isFinite(usedFrac) && usedFrac > 0;
  const nowTempC = Number(device.temperature);
  const ratedDays = hasIntegrated && Number.isFinite(nowTempC)
    ? elaRatedYears({ advIntervalS: fam.advIntervalS, txDbm: fam.txDbm, tempC: nowTempC }) * 365
    : bleRatedDays(fam);
  const ratePerDay = capacity ? capacity / ratedDays : 0;

  let remainingDays = Math.max(0, ratedDays - daysSinceEpoch);
  let consumed = capacity ? Math.min(capacity, ratePerDay * daysSinceEpoch) : 0;
  let method = 'timeBased';
  const notes = [];

  if (hasIntegrated) {
    // Bookings stop at the last sighting, but the cell does not. Charge the
    // stretch since then at read time, or a tag nobody has heard from for
    // months would look fresher than one reporting every hour.
    const bookedTo = Date.parse(device.battUsedAt || device.battEpochAt);
    const unbooked = Number.isFinite(bookedTo) ? Math.max(0, (now - bookedTo) / MS_PER_DAY) : 0;
    const totalFrac = Math.min(1, usedFrac
      + elaLifeFraction(unbooked, Number.isFinite(nowTempC) ? nowTempC : 23, fam));
    if (unbooked > 7) {
      notes.push(`Not seen for ${Math.round(unbooked)} days - that stretch is charged at the `
        + `reference temperature, since nothing was reported from it.`);
    }
    remainingDays = Math.max(0, ratedDays * (1 - totalFrac));
    consumed = capacity ? capacity * totalFrac : consumed;
    method = 'temperatureIntegrated';
    if (Number.isFinite(nowTempC)) {
      notes.push(`Life booked against the temperature this tag actually reported; `
        + `remaining days assume it stays near ${Math.round(nowTempC)} \u00b0C.`);
    }
  }

  const soc = bleSocFromVoltage(fam, Number(device.battery));
  if (soc != null) {
    const byVoltageDays = soc * ratedDays;
    if (byVoltageDays < remainingDays) {
      remainingDays = byVoltageDays;
      consumed = capacity ? capacity * (1 - soc) : consumed;
      method = 'timeBased+voltage';
      notes.push(`Cell voltage ${Number(device.battery).toFixed(2)} V - the reading caps the estimate.`);
    }
  }
  if (fam.estimated) notes.push(`${fam.label} rated life is an engineering estimate at a ${fam.advIntervalS ? fam.advIntervalS + ' s' : 'default'} advertising interval - not yet confirmed by the manufacturer.`);
  if (device.battEpochKind === 'firstContact' || !device.battEpochKind) notes.push('Counting since first contact; time on the shelf before that is unknown.');

  return {
    modelVersion: Number(device.battModelVersion) || BATTERY_MODEL_VERSION,
    familyKey, family: fam.label, cell: fam.cell,
    method,
    epochAt: device.battEpochAt, epochKind: device.battEpochKind || 'firstContact',
    daysSinceEpoch, uplinks: Number(device.battUplinks) || 0,
    capacityMAh: capacity, usableMAh: usable, consumedMAh: consumed, remainingMAh: Math.max(0, usable - consumed),
    consumedPct: ratedDays > 0 ? Math.min(100, 100 * (1 - remainingDays / ratedDays)) : 0,
    ratePerDay, rateSource: 'rated', rate30: ratePerDay, rateLife: ratePerDay, windowCoverageDays: daysSinceEpoch,
    ratedDays,
    estRemainingDays: remainingDays,
    confidence: 'low', notes,
  };
}

/** "~14 months (estimate)" style label for the UI. */
export function formatRemaining(est) {
  if (!est) return null;
  if (est.method === 'externalPower') return 'External power';
  const d = est.estRemainingDays;
  if (!isFinite(d)) return 'n/a';
  if (d <= 0) return 'Depleted';
  if (d >= 3650) return '10+ years';
  if (d >= 730) return `~${(d / 365).toFixed(1)} years`;
  if (d >= 365) return `~${(d / 365).toFixed(1)} year`;
  if (d >= 60)  return `~${Math.round(d / 30)} months`;
  if (d >= 14)  return `~${Math.round(d / 7)} weeks`;
  return `~${Math.round(d)} days`;
}
